import { formatSourceCitationDate } from '../../utils/sourceCitationDate';
import { isCaregiverAuthoredProfileFact } from '../knowledgeItems/caregiverProfileFact';

export const PROFILE_SOURCE_KINDS = Object.freeze({
  CAREGIVER_DIRECT: 'caregiver_direct',
  CAREGIVER_NOTE: 'caregiver_note',
  EXTERNAL: 'external',
  UNKNOWN: 'unknown',
});

const CAREGIVER_SOURCE_LABELS = Object.freeze({
  care_knowledge: 'Care Knowledge',
  typed_long_note: 'Caregiver note',
  quick_check_in: 'Quick check-in',
  dictated_text: 'Dictated note',
  voice: 'Voice note',
});

const EXTERNAL_SOURCE_LABELS = Object.freeze({
  care_knowledge_document: 'Uploaded document',
  care_knowledge_photo: 'Photo',
  care_knowledge_screenshot: 'Screenshot',
  profile_document: 'Uploaded document',
  doctor_note: 'Clinical note',
  file_import: 'Imported file',
  ocr_text: 'Scanned document',
  email: 'Email',
  incoming_email: 'Email',
  forwarded_email: 'Email',
});

const GENERIC_SOURCE_TITLES = new Set(['typed care info', 'care knowledge', 'care source', 'source']);
const PROVENANCE_LABELS = Object.freeze({
  caregiver_typed: 'Caregiver-typed',
  imported_document: 'Imported document',
  ai_extracted: 'AI-extracted',
});
const cleanText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const formatSourceDate = (value) => {
  if (!value) return '';
  const raw = cleanText(value);
  if (/^\d{4}-\d{2}$/.test(raw)) {
    const [year, month] = raw.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
  const date = value?.toDate?.() || new Date(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const getPrimarySourceRef = (fact = {}) => {
  const refs = Array.isArray(fact.sourceRefs) ? fact.sourceRefs : [];
  return refs.find((ref) => ref?.sourceTitle || ref?.sourceType || ref?.sourceText || ref?.sourceContext)
    || refs[0]
    || {};
};

const getSourceDateValue = (sourceRef = {}) => (
  sourceRef.capturedAt || sourceRef.confirmedAt || sourceRef.createdAt || null
);

const getSourceTime = (sourceRef = {}) => {
  const value = getSourceDateValue(sourceRef);
  const date = value?.toDate?.() || new Date(value || 0);
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
};

const getLatestSourceRef = (fact = {}) => {
  const refs = Array.isArray(fact.sourceRefs) ? fact.sourceRefs : [];
  return refs.reduce((latest, sourceRef) => {
    return getSourceTime(sourceRef) > getSourceTime(latest) ? sourceRef : latest;
  }, refs[0] || {});
};

const getEvidencePresentation = (fact = {}) => {
  const refs = Array.isArray(fact.sourceRefs) ? fact.sourceRefs : [];
  return {
    ...(refs.length > 1 ? {
      supportingSourceCount: refs.length,
      latestSourceDateLabel: formatSourceDate(
        getSourceDateValue(getLatestSourceRef(fact)) || fact.updatedAt
      ),
    } : {}),
    ...(fact.hasConflictingEvidence === true ? { needsReview: true } : {}),
  };
};

const getSourceProvenance = (fact = {}) => (
  fact?.sourceProvenance && typeof fact.sourceProvenance === 'object' && !Array.isArray(fact.sourceProvenance)
    ? fact.sourceProvenance
    : {}
);

const hasSourceReferenceDetails = (sourceRef = {}) => Boolean(sourceRef.sourceText || sourceRef.sourceContext || sourceRef.sourceTitle);

const getUsefulTitle = (sourceRef = {}) => {
  const title = cleanText(sourceRef.sourceTitle || sourceRef.title);
  return title && !GENERIC_SOURCE_TITLES.has(title.toLowerCase()) ? title : '';
};

const buildSourcePresentation = (fact = {}) => {
  const sourceRef = getPrimarySourceRef(fact);
  const provenance = getSourceProvenance(fact);
  const evidencePresentation = getEvidencePresentation(fact);
  const dateLabel = formatSourceDate(
    sourceRef.capturedAt || sourceRef.confirmedAt || sourceRef.createdAt || fact.capturedAt || fact.createdAt
  );

  if (provenance.origin) {
    const origin = cleanText(provenance.origin).toLowerCase();
    const originLabel = PROVENANCE_LABELS[origin] || cleanText(provenance.label) || 'Care record';
    return {
      ...evidencePresentation,
      originLabel,
      dateLabel,
      sourceKind: origin === 'caregiver_typed'
        ? PROFILE_SOURCE_KINDS.CAREGIVER_DIRECT
        : origin === 'imported_document'
          ? PROFILE_SOURCE_KINDS.EXTERNAL
          : PROFILE_SOURCE_KINDS.UNKNOWN,
      actionLabel: origin === 'caregiver_typed'
        ? ''
        : hasSourceReferenceDetails(sourceRef)
          ? 'View source'
          : '',
      ...(origin === 'caregiver_typed'
        ? { contentLabel: 'Your original note', isLongOriginalEntry: cleanText(fact.summary || fact.title).length > 140 }
        : {}),
    };
  }

  if (isCaregiverAuthoredProfileFact(fact)) {
    const originalText = cleanText(fact.summary || fact.title);
    return {
      ...evidencePresentation,
      originLabel: 'Added by you',
      dateLabel,
      sourceKind: PROFILE_SOURCE_KINDS.CAREGIVER_DIRECT,
      actionLabel: '',
      contentLabel: 'Your original note',
      isLongOriginalEntry: originalText.length > 140,
    };
  }

  const sourceType = cleanText(sourceRef.sourceType).toLowerCase();
  const sourceTitle = getUsefulTitle(sourceRef);

  if (CAREGIVER_SOURCE_LABELS[sourceType]) {
    return {
      ...evidencePresentation,
      originLabel: `From ${sourceTitle || CAREGIVER_SOURCE_LABELS[sourceType]}`,
      dateLabel,
      sourceKind: PROFILE_SOURCE_KINDS.CAREGIVER_NOTE,
      actionLabel: sourceRef.sourceText || sourceRef.sourceContext ? 'View source' : '',
    };
  }

  if (EXTERNAL_SOURCE_LABELS[sourceType] || sourceTitle) {
    return {
      ...evidencePresentation,
      originLabel: `From ${sourceTitle || EXTERNAL_SOURCE_LABELS[sourceType] || 'Care record'}`,
      dateLabel,
      sourceKind: PROFILE_SOURCE_KINDS.EXTERNAL,
      actionLabel: sourceRef.sourceText || sourceRef.sourceContext ? 'View source' : '',
    };
  }

  // Older Knowledge facts do not always carry enough source metadata to say
  // where they came from. Leave those items unlabeled instead of presenting a
  // guessed origin as reliable provenance.
  return undefined;
};

export const buildProfileSourcePresentation = (fact = {}) => {
  const presentation = buildSourcePresentation(fact);
  const sourceRef = getPrimarySourceRef(fact);
  if (!sourceRef.sourceId || sourceRef.sourceCollection !== 'careSources') return presentation;
  return {
    ...presentation,
    originLabel: sourceRef.authorName || 'Author not recorded',
    dateLabel: formatSourceCitationDate(sourceRef.capturedAt || sourceRef.createdAt),
    sourceId: sourceRef.sourceId,
    actionLabel: 'View source',
    ...(fact.reviewedByName && fact.reviewedAt ? {approvalLabel:`Approved by ${fact.reviewedByName} · ${formatSourceCitationDate(fact.reviewedAt)}`} : {}),
  };
};
